import arrow from '../assets/svg/dropdownarrow.svg';
import { breakpoints, colors } from '../styles';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface MoreInfoQuestion {
    title: string;
    placeholder: string;
    answers?: string[];
    keyString: string;
    key: string;
    currentAnswer: string;
    setCurrentAnswer: (arg0: string) => void;
    defaultValue?: string;
    returnIndex?: boolean;
    noResultsText?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
}

const MoreInfoQuestionWrapper = styled.div``;

const Title = styled.div``;

const Dropdown = styled.select`
    appearance: none;
    border: 1px solid ${colors.libertyGray10};
    border-radius: 4px;
    background: white;
    color: ${colors.textColor1};
    width: 100%;
    height: 50px;
    padding: 15px;
    font-size: 13.5px;
    box-sizing: border-box;
    background-image: url('${arrow}');
    background-repeat: no-repeat;
    background-position-x: calc(100% - 10px);
    background-position-y: center;
    text-overflow: ellipsis;
    ${breakpoints.onlyMobile} {
        font-size: 13px;
    }
    ${breakpoints.sm} {
        width: 50%;
    }
    ${breakpoints.largeDesktop} {
        width: 85%;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Input = styled.input.attrs(() => ({
    type: 'text',
}))`
    appearance: none;
    border: 1px solid ${colors.white20};
    border-radius: 8px;
    background: white;
    color: ${colors.textColor1};
    width: 100%;
    height: 50px;
    padding: 15px;
    font-size: 0.75em;
    box-sizing: border-box;
    ${breakpoints.onlyMobile} {
        font-size: 13px;
    }
`;

const ColoredOption = styled.option`
    ${breakpoints.onlyMobile} {
        font-size: 13px;
        width: 100%;
    }
    color: ${colors.black};
`;

const MoreInfoFreetext: React.FC<MoreInfoQuestion> = ({
    title,
    placeholder,
    keyString,
    currentAnswer,
    setCurrentAnswer,
    defaultValue = '',
}): React.ReactElement => (
    <MoreInfoQuestionWrapper key={keyString}>
        <Title>{title}</Title>
        <Input
            placeholder={placeholder}
            value={currentAnswer || defaultValue}
            onChange={(e) => setCurrentAnswer(e.currentTarget.value)}
        ></Input>
    </MoreInfoQuestionWrapper>
);

const MoreInfoDropdown: React.FC<MoreInfoQuestion> = ({
    title,
    placeholder,
    answers = [],
    keyString,
    currentAnswer,
    setCurrentAnswer,
    returnIndex = false,
    disabled = false,
}): React.ReactElement => (
    <MoreInfoQuestionWrapper key={keyString}>
        <Title dangerouslySetInnerHTML={{ __html: title }} />
        <Dropdown
            onChange={(e) =>
                setCurrentAnswer(
                    returnIndex
                        ? e.currentTarget.selectedIndex.toString()
                        : e.currentTarget.value,
                )
            }
            defaultValue={(!currentAnswer && placeholder) || undefined}
            value={currentAnswer || undefined}
            disabled={disabled}
        >
            <ColoredOption disabled={true}>{placeholder}</ColoredOption>
            {answers.map((answer) => (
                <ColoredOption key={answer}>{answer}</ColoredOption>
            ))}
        </Dropdown>
    </MoreInfoQuestionWrapper>
);

const SearchableDropdownWrapper = styled.div`
    position: relative;
    width: 100%;
    ${breakpoints.sm} {
        width: 50%;
    }
    ${breakpoints.largeDesktop} {
        width: 85%;
    }
`;

const SelectDisplay = styled.div`
    appearance: none;
    border: 1px solid ${colors.libertyGray10};
    border-radius: 4px;
    background: white;
    color: ${colors.textColor1};
    width: 100%;
    height: 50px;
    padding: 15px;
    font-size: 13.5px;
    box-sizing: border-box;
    background-image: url('${arrow}');
    background-repeat: no-repeat;
    background-position-x: calc(100% - 10px);
    background-position-y: center;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    cursor: pointer;
    ${breakpoints.onlyMobile} {
        font-size: 13px;
    }
`;

const DropdownPanel = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 10;
    background: white;
    border: 1px solid ${colors.lightGray};
`;

const SearchInput = styled.input.attrs(() => ({
    type: 'text',
}))`
    width: 100%;
    padding: 4px 8px;
    font-size: 13px;
    border: none;
    border-bottom: 1px solid ${colors.libertyGray10};
    box-sizing: border-box;
    outline: none;
`;

const OptionsList = styled.ul`
    margin: 0;
    padding: 1px 0;
    list-style: none;
    max-height: 300px;
    overflow-y: auto;
`;

const OptionItem = styled.li<{ isSelected?: boolean }>`
    padding: 1px 8px;
    font-size: 13px;
    color: ${(props) => (props.isSelected ? colors.white : colors.black)};
    background-color: ${(props) =>
        props.isSelected ? '#0078d7' : 'transparent'};
    cursor: default;
    &:hover {
        background-color: #0078d7;
        color: ${colors.white};
    }
`;

const NoResults = styled.li`
    padding: 1px 8px;
    font-size: 13px;
    color: ${colors.lightGray};
`;

const MoreInfoSearchableDropdown: React.FC<MoreInfoQuestion> = ({
    title,
    placeholder,
    answers = [],
    keyString,
    currentAnswer,
    setCurrentAnswer,
    noResultsText = '',
    searchPlaceholder = '',
    disabled = false,
}): React.ReactElement => {
    const [searchText, setSearchText] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setSearchText('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredAnswers = answers.filter((answer) => {
        return (
            answer && answer.toLowerCase().includes(searchText.toLowerCase())
        );
    });

    const handleSelect = (answer: string) => {
        if (disabled) return;
        setCurrentAnswer(answer);
        setIsOpen(false);
        setSearchText('');
    };

    const handleToggle = () => {
        if (disabled) return;
        const opening = !isOpen;
        setIsOpen(opening);
        if (opening) {
            setSearchText('');
            setTimeout(() => searchInputRef.current?.focus(), 0);
        }
    };

    return (
        <MoreInfoQuestionWrapper key={keyString}>
            <Title>{title}</Title>
            <SearchableDropdownWrapper ref={wrapperRef}>
                <SelectDisplay
                    onClick={handleToggle}
                    style={
                        disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}
                    }
                >
                    {currentAnswer || placeholder}
                </SelectDisplay>
                {isOpen && (
                    <DropdownPanel>
                        <SearchInput
                            ref={searchInputRef}
                            placeholder={searchPlaceholder}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <OptionsList>
                            {filteredAnswers.length > 0 ? (
                                filteredAnswers.map((answer) => (
                                    <OptionItem
                                        key={answer}
                                        isSelected={answer === currentAnswer}
                                        onMouseDown={() => handleSelect(answer)}
                                    >
                                        {answer}
                                    </OptionItem>
                                ))
                            ) : (
                                <NoResults>{noResultsText}</NoResults>
                            )}
                        </OptionsList>
                    </DropdownPanel>
                )}
            </SearchableDropdownWrapper>
        </MoreInfoQuestionWrapper>
    );
};

export { MoreInfoFreetext, MoreInfoDropdown, MoreInfoSearchableDropdown };
